import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthRouting, useSignedInUserState } from "../hooks/useAuthState";
import { restaurantService } from "../services/firebase/restaurantManagementService";
import { FullProperty } from "../models/fullProperty";
import { SaleItem } from "../models/saleItem";
import { toast } from "react-toastify";
import { Badge, Box, Button, CircularProgress, Drawer, IconButton, List, ListItem, ListItemButton, ListItemText, Modal, TextField, Typography } from "@mui/material";
import { StringsService } from "../services/strings/strings";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ChatIcon from '@mui/icons-material/Chat';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import RestaurantOrderHistoryPage from "../profilePage/restaurantOrderHistory/restaurantOrderHistoryPage";
import { CartService } from "../services/functions/cartService";
import MaintainSpecificRestaurantPage from "../profilePage/maintainSpecificRestaurant/maintainSpecificRestaurantPage";
import { AuthButton } from "../shared/authButton";
import { BillingPage } from "./billing";
import MessagesPage from "../messagesPage/messagesPage";
import MailIcon from "@mui/icons-material/Mail";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useActiveMessages } from "../hooks/useUnreadMessages";
import { useRestaurantOrderHistory } from "../hooks/useRestaurantOrderHistory";
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { CreateRestaurantPage } from "../restaurant/createRestaurantPage";
import SingleRestaurantStaffManagement from "../profilePage/singleRestaurantStaffManagement/singleRestaurantStaffManagement";
import { requestPermissionAndGetToken } from "../shared/messagesConsent";
import { CommentsPage } from "./comments/comments";
import { OrderDecisionRequestType } from "../models/CartServiceType";

type dashboardStates = "Orders" | "Bags" | "Billing" | "Notifications" | "Restaurant" | "RestaurantStaff" | "Comments"

function DashboardPage() {
  const [restaurant, setRestaurants] = useState<FullProperty | null>(null);
  const [drawerOptions, setDrawerOptions] = useState<dashboardStates[]>([]);
  const [restaurantSaleItems, setRestaurantSaleItems] = useState<{ [restaurantId: string]: SaleItem[] }>({});
  const [locallyLoading, setLocallyLoading] = useState<boolean>(true);
  const [selectedDrawerItem, setSelectedDrawerItem] = useState<dashboardStates>("Orders");
  const navigate = useNavigate();
  const { user, profile } = useSignedInUserState();
  const [openModal, setOpenModal] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [orderDetails, setOrderDetails] = useState<{ orderId: string, orderSaleItems: string } | null>(null);
  const [isProcessingCollection, setIsProcessingCollection] = useState<boolean>(false);
  const { activeCount, loading: messagesLoading, messages, setMessages } = useActiveMessages(profile);
  const { pendingOrders, collectedOrders, loading: restaurantOrderLoading, error, refreshOrders, menuData } = useRestaurantOrderHistory(restaurant?.id ?? "");

  useEffect(() => {
    if (profile) {
      requestPermissionAndGetToken(profile);
    }
  }, [profile]);


  const fetchManagedRestaurants = async () => {
    try {
      setLocallyLoading(false);
      if (!user || !user.uid || !profile) {
        return;
      }
      setLocallyLoading(true);
      let data: FullProperty[] = [];
      if (profile.profileType === "staff") {
        setDrawerOptions(["Orders", "Bags", "Notifications"] as dashboardStates[]);
        data = await restaurantService.fetchRestaurantsByStaff(user.uid);
      } else if (profile.profileType === "owner" || profile.profileType === "admin") {
        setDrawerOptions(["Orders", "Bags", "Notifications", "Restaurant", "Billing", "RestaurantStaff", "Comments"] as dashboardStates[]);
        data = await restaurantService.fetchRestaurantsByOwner(user.uid);
      } else {
        navigate("/search");
      }
      console.log("restaurants: ", data)
      setRestaurants(data[0]);

      const saleItemsPromises = data.map(async (restaurant) => {
        const saleItems = await restaurantService.fetchSaleItems(restaurant.id);
        return { [restaurant.id]: saleItems };
      });

      const resolvedSaleItems = await Promise.all(saleItemsPromises);
      setRestaurantSaleItems(resolvedSaleItems.reduce((acc, curr) => ({ ...acc, ...curr }), {}));
    } catch (err) {
      toast.error("Failed to fetch managed restaurants.");
    } finally {
      setLocallyLoading(false);
    }
  };

  useEffect(() => {

    fetchManagedRestaurants();
  }, [user?.uid, profile?.profileType]);

  useAuthRouting();


  const handleDrawerItemClick = (item: dashboardStates) => {
    setSelectedDrawerItem(item);
  };

  // Handle Process Collection modal submission
  const handleProcessCollection = async () => {
    setIsProcessingCollection(true);
    const collectOrder = await CartService.collectOrder({
      orderNumberId: parseInt(confirmationNumber),
      restaurantId: restaurant?.id ?? ""
    });
    if (collectOrder.success && collectOrder.order) {
      setConfirmationNumber("");
      var orderId = collectOrder.order.id;
      var orderDescription = ``;
      var promises = collectOrder.order.items.map(async (orderItem) => {
        orderDescription += `${orderItem.quantity}x ${orderItem.menuCartItem.name}\n`
      })
      await Promise.all(promises)
      setOrderDetails({ orderId: orderId, orderSaleItems: orderDescription });
    } else {
      alert(collectOrder.error);
    }
    setIsProcessingCollection(false)
    refreshOrders()
  };

  const handleAccepRejectOrderDecision = async (request: OrderDecisionRequestType) => {
    console.error("is loading", locallyLoading)
    try {
      setLocallyLoading(false);
      if (!user || !user.uid || !profile) {
        return;
      }
      setLocallyLoading(true);
      await CartService.acceptRejectOrder(request);
      await requestPermissionAndGetToken(profile);
    } catch (err) {
      console.log(err)
      toast.error("Failed to fetch managed restaurants.");
    } finally {
      setLocallyLoading(false);
    }
  }

  return (
    // <Box sx={{ display: "flex", minHeight: "100vh", }} className={'patlite'}>
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "row" }} className={'patlite'}>
      {/* Proccess Prder */}
      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setConfirmationNumber("");
          setOrderDetails(null);
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
          className="patlite"
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Process Order Collection
          </Typography>
          <TextField
            fullWidth
            label="Confirmation Number"
            value={confirmationNumber}
            onChange={(e) => !isProcessingCollection ? setConfirmationNumber(e.target.value) : null}
            margin="normal"
            disabled={!!orderDetails}
          />

          {isProcessingCollection ? (
            <CircularProgress size={24} sx={{ color: "black" }} />
          ) : (
            <Button
              variant="contained"
              onClick={handleProcessCollection}
              fullWidth
              sx={{
                mt: 1,
                background: "#162C1F",
                border: "1px solid #162C1F",
                borderRadius: "24px",
              }}
            >
              Confirm
            </Button>
          )}
          {orderDetails && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                Order #{orderDetails.orderId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Items: {orderDetails.orderSaleItems}
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>
      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: 140,
          color: "#000",
          flexShrink: 0,
          height: "100vh",
          [`& .MuiDrawer-paper`]: {
            width: 140,
            boxSizing: "border-box",
            backgroundColor: "#f5f9e9",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100vh', // ensure full height for scroll
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Scrollable + Centered List Wrapper */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <List
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                margin: 'auto 0', // center vertically when there's space
                width: '100%',
                gap: 1,
              }}
            >
              {drawerOptions.map((item, index) => {
                const isSelected = selectedDrawerItem === item;
                const iconStyles = isSelected ? { color: 'white', fontSize: 32, mb: 1 } : { fontSize: 32, mb: 1 };
                const textStyles = isSelected ? { color: 'white', fontSize: '14px' } : { fontSize: '14px' };

                return (
                  <ListItem
                    key={index}
                    disablePadding
                    sx={{
                      width: '100%',
                      justifyContent: 'center',
                      backgroundColor: isSelected ? '#162C1F' : 'transparent',
                      borderRadius: 1,
                    }}
                  >
                    <ListItemButton
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        padding: 1,
                      }}
                      onClick={() => handleDrawerItemClick(item)}
                    >
                      {item === "Orders" && (
                        <Badge badgeContent={pendingOrders.length > 0 ? pendingOrders.length : null} color="error">
                          <ReceiptLongIcon sx={iconStyles} />
                        </Badge>
                      )}
                      {item === "Bags" && <ShoppingBagIcon sx={iconStyles} />}
                      {item === "Notifications" &&
                        <Badge badgeContent={activeCount > 0 ? activeCount : null} color="error">
                          <MailIcon sx={iconStyles} />
                        </Badge>
                      }
                      {item === "Restaurant" && <StorefrontIcon sx={iconStyles} />}
                      {item === "Billing" && <CreditCardIcon sx={iconStyles} />}
                      {item === "RestaurantStaff" && <ManageAccountsIcon sx={iconStyles} />}
                      {item === "Comments" && <ChatIcon sx={iconStyles} />}
                      <ListItemText primary={item} primaryTypographyProps={textStyles} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>

          {/* Auth Button stays at the bottom */}
          <Box sx={{ p: 1 }}>
            <AuthButton />
          </Box>
        </Box>
      </Drawer>


      {/* Main Content */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 1000,
          overflow: 'auto'
        }}
      >
        <IconButton
          onClick={() => setOpenModal(true)}
          sx={{
            backgroundColor: '#162C1F',
            border: '1px solid #162C1F',
            borderRadius: '50%',
            width: 56,
            height: 56,
            color: '#fff',
            '&:hover': {
              backgroundColor: '#1e3b29',
            },
          }}
        >
          <AssignmentTurnedInIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {selectedDrawerItem === "Orders" && (
          <RestaurantOrderHistoryPage
            restaurantId={restaurant?.id}
            pendingOrders={pendingOrders}
            menuData={menuData}
            collectedOrders={collectedOrders}
            loading={restaurantOrderLoading || locallyLoading}
            error={error!}
            profile={profile}
            accepRejectOrderDecision={handleAccepRejectOrderDecision}
          />
        )}
        {selectedDrawerItem === "Bags" && restaurant && <MaintainSpecificRestaurantPage restaurant={restaurant!} />}
        {selectedDrawerItem === "Billing" && restaurant && (profile?.profileType == "owner" || profile?.profileType == "admin") && <BillingPage restaurant={restaurant} />}
        {selectedDrawerItem === "Notifications" && <MessagesPage dashboardMessagesLoading={messagesLoading} dashboardMessages={messages} dashboardSetMessages={setMessages} />}
        {selectedDrawerItem === "Restaurant" && (profile?.profileType == "owner" || profile?.profileType == "admin") && <CreateRestaurantPage
          editRestaurant={restaurant}
          onRestaurantUpdate={(newRestaurant: FullProperty) => setRestaurants(newRestaurant)} />}
        {selectedDrawerItem === "RestaurantStaff" && restaurant && (profile?.profileType == "owner" || profile?.profileType == "admin") && <SingleRestaurantStaffManagement restaurant={restaurant} updateRestaurant={fetchManagedRestaurants} />}
        {selectedDrawerItem === "Comments" && restaurant && profile && <CommentsPage restaurant={restaurant} profile={profile} />}
      </Box>
    </Box>
  );
}

export default DashboardPage;